
import { Ball, SimulationConfig, Vector2D, ShapeType } from '@/types';
import { random } from '@/lib/random';

/**
 * Advanced Spawner Logic for Hypnotic Ball Simulation
 */
export class SpawnerPhysics {

    public static clampVelocity(vel: Vector2D, config: SimulationConfig): Vector2D {
        const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y);
        const max = config.maxVelocityLimit || 50;
        const min = config.minVelocityLimit || 1;

        if (speed < 0.001) {
            // Nudge stuck balls
            const angle = random.range(0, Math.PI * 2);
            return { x: Math.cos(angle) * min, y: Math.sin(angle) * min };
        }

        if (speed > max) {
            return { x: (vel.x / speed) * max, y: (vel.y / speed) * max };
        }
        if (speed < min) {
            return { x: (vel.x / speed) * min, y: (vel.y / speed) * min };
        }
        return vel;
    }

    /**
     * Generates a velocity vector based on the global initial speed setting.
     * This ensures all spawns across the app follow a "Global Standard".
     */
    public static getSpawnVelocity(config: SimulationConfig, angle?: number, speedOverride?: number): Vector2D {
        const globalRefSpeed = (config.initialSpeed || 1.0) * 12;
        const baseSpeed = speedOverride !== undefined ? speedOverride : globalRefSpeed;
        
        let targetAngle = angle;
        if (targetAngle === undefined) {
             const angleSpread = (config.ballPlacementRandomDirection ? 360 : (config.spawnerVelocitySpread || 15)) * (Math.PI / 180);
             const baseAngle = (config.ballPlacementDirectionAngle || 90) * (Math.PI / 180);
             targetAngle = baseAngle + (random.range(-angleSpread/2, angleSpread/2));
        }

        const vel = {
            x: Math.cos(targetAngle) * baseSpeed,
            y: Math.sin(targetAngle) * baseSpeed
        };
        
        return this.clampVelocity(vel, config);
    }

    /**
     * Handles split-logic (Fission) when a ball hits something or is triggered
     */
    public static handleFission(
        parent: Ball, 
        config: SimulationConfig, 
        now: number, 
        createBall: (overrides: Partial<Ball>) => Ball
    ): Ball[] {
        if (!config.fissionEnabled) return [];
        if ((parent.generation || 0) >= config.fissionMaxDepth) return [];
        if (parent.radius < config.fissionMinRadius) return [];

        const children: Ball[] = [];
        const splitCount = config.fissionSplitCount || 2;
        const newRadius = parent.radius * (config.fissionSizeMultiplier || 0.7);
        const nextGen = (parent.generation || 0) + 1;
        const baseSpeed = Math.sqrt(parent.vel.x**2 + parent.vel.y**2) * (config.fissionVelocityBoost || 1.2);
        const baseAngle = Math.atan2(parent.vel.y, parent.vel.x);
        const spread = (config.fissionAngleOffset || 30) * (Math.PI / 180);

        for (let i = 0; i < splitCount; i++) {
            const angleOffset = (i - (splitCount - 1) / 2) * spread;
            const angle = baseAngle + angleOffset;
            
            const childVelocity = this.clampVelocity({
                x: Math.cos(angle) * baseSpeed,
                y: Math.sin(angle) * baseSpeed
            }, config);

            children.push(createBall({
                id: `split-${parent.id}-${nextGen}-${i}-${now}`,
                pos: { ...parent.pos },
                vel: childVelocity,
                radius: newRadius,
                mass: config.fusionMassConservation ? (parent.mass / splitCount) : parent.mass,
                color: parent.color,
                generation: nextGen,
                spawnTime: now
            }));
        }

        return children;
    }

    /**
     * Handles merging logic (Fusion) when two balls collide
     */
    public static handleFusion(
        b1: Ball,
        b2: Ball,
        config: SimulationConfig
    ): { merged: boolean; survivor?: Ball; removed?: Ball } {
        if (!config.fusionEnabled) return { merged: false };
        
        // Only merge if total radius doesn't exceed limit
        const combinedArea = Math.PI * (b1.radius ** 2 + b2.radius ** 2);
        const newRadius = Math.sqrt(combinedArea / Math.PI);
        
        if (newRadius > config.fusionMaxRadius) return { merged: false };

        // Larger ball survives
        const survivor = b1.radius >= b2.radius ? b1 : b2;
        const removed = b1.radius >= b2.radius ? b2 : b1;

        if (config.fusionMassConservation) {
            const totalMass = b1.mass + b2.mass;
            // Momentum conservation: m1*v1 + m2*v2 = (m1+m2)*v_final
            const newVel = {
                x: (b1.mass * b1.vel.x + b2.mass * b2.vel.x) / totalMass,
                y: (b1.mass * b1.vel.y + b2.mass * b2.vel.y) / totalMass
            };
            const clamped = this.clampVelocity(newVel, config);
            survivor.vel.x = clamped.x;
            survivor.vel.y = clamped.y;
            survivor.mass = totalMass;
        }

        survivor.radius = newRadius;
        
        return { merged: true, survivor, removed };
    }

    /**
     * Handles pattern-based spawning (Burst, Spiral, etc.)
     */
    public static handlePatternSpawn(
        config: SimulationConfig,
        center: Vector2D,
        now: number,
        createBall: (overrides: Partial<Ball>) => Ball,
        hue: number = 0
    ): Ball[] {
        const balls: Ball[] = [];
        const count = config.spawnerBurstCount || 10;
        let pattern = config.spawnerPattern || 'BURST';
        if (config.spawnerEngineMode === 'CHAOS') pattern = 'CHAOS';
        const startRadius = config.ballRadius;

        switch (pattern) {
            case 'BURST':
            case 'RADIAL':
                for (let i = 0; i < count; i++) {
                    const angle = (i / count) * Math.PI * 2;
                    const vel = this.getSpawnVelocity(config, angle);
                    balls.push(createBall({
                        id: `pattern-${pattern}-${i}-${now}`,
                        pos: { ...center },
                        vel: vel,
                        radius: startRadius,
                        color: config.useBallRGB ? `hsl(${hue}, 100%, 50%)` : config.ballColor,
                        spawnTime: now
                    }));
                }
                break;
            case 'SPIRAL':
                for (let i = 0; i < count; i++) {
                    const angle = (i / count) * Math.PI * 4; // Two rotations
                    const dist = (i / count) * 50;
                    const speed = (config.initialSpeed * 12) * (0.5 + (i / count));
                    const vel = this.getSpawnVelocity(config, angle, speed);
                    balls.push(createBall({
                        id: `pattern-spiral-${i}-${now}`,
                        pos: { 
                            x: center.x + Math.cos(angle) * dist, 
                            y: center.y + Math.sin(angle) * dist 
                        },
                        vel: vel,
                        radius: startRadius,
                        color: config.useBallRGB ? `hsl(${hue}, 100%, 50%)` : config.ballColor,
                        spawnTime: now
                    }));
                }
                break;
            case 'GRID':
                const cols = Math.ceil(Math.sqrt(count));
                const rows = Math.ceil(count / cols);
                const spacing = startRadius * 3;
                for (let r = 0; r < rows; r++) {
                    for (let c = 0; c < cols; c++) {
                        if (balls.length >= count) break;
                        const px = center.x + (c - (cols-1)/2) * spacing;
                        const py = center.y + (r - (rows-1)/2) * spacing;
                        const vel = this.getSpawnVelocity(config);
                        balls.push(createBall({
                            id: `pattern-grid-${r}-${c}-${now}`,
                            pos: { x: px, y: py },
                            vel: vel,
                            radius: startRadius,
                            color: config.useBallRGB ? `hsl(${hue}, 100%, 50%)` : config.ballColor,
                            spawnTime: now
                        }));
                    }
                }
                break;
            case 'WAVE':
                for (let i = 0; i < count; i++) {
                    const offset = (i - (count-1)/2) * (startRadius * 2.5);
                    const vel = this.getSpawnVelocity(config, Math.PI / 2); // Downward
                    balls.push(createBall({
                        id: `pattern-wave-${i}-${now}`,
                        pos: { x: center.x + offset, y: center.y },
                        vel: vel,
                        radius: startRadius,
                        color: config.useBallRGB ? `hsl(${hue}, 100%, 50%)` : config.ballColor,
                        spawnTime: now
                    }));
                }
                break;
            case 'CHAOS':
                for (let i = 0; i < count; i++) {
                    // Chaos still follows the speed standard but with a small random jitter
                    const jitter = random.range(0.8, 1.2);
                    const speed = (config.initialSpeed * 12) * jitter;
                    const vel = this.getSpawnVelocity(config, undefined, speed);
                    balls.push(createBall({
                        id: `pattern-chaos-${i}-${now}`,
                        pos: { 
                            x: center.x + random.range(-100, 100), 
                            y: center.y + random.range(-100, 100) 
                        },
                        vel: vel,
                        radius: startRadius * random.range(0.5, 1.5),
                        color: config.useBallRGB ? `hsl(${random.range(0, 360)}, 100%, 50%)` : config.ballColor,
                        spawnTime: now
                    }));
                }
                break;
        }

        return balls;
    }

    /**
     * Updates ball lifecycle (lifespan, fading)
     */
    public static updateLifecycle(
        balls: Ball[],
        config: SimulationConfig,
        now: number,
        deltaTime: number,
        onDeath?: (b: Ball) => void
    ): Ball[] {
        if (!config.ballLifespanEnabled) {
            // Restore defaults if disabled
            balls.forEach(b => {
                b.opacity = 1;
                b.isFading = false;
            });
            return balls;
        }

        const lifespanMs = (config.ballLifespan || 5) * 1000;
        
        return balls.filter(b => {
            const age = now - (b.spawnTime || now);
            const remaining = lifespanMs - age;

            if (remaining <= 0) {
                if (onDeath) onDeath(b);
                return false;
            }

            // Calculate opacity
            const fadeStart = lifespanMs * 0.3; // Fade out in last 30%
            if (remaining < fadeStart) {
                const pr = remaining / fadeStart;
                b.opacity = Math.pow(pr, config.ballFadeOutPower || 1.0);
                b.isFading = true;
                
                if (config.ballShrinkOnDeath) {
                    b.radius = Math.max(0.5, b.radius * 0.98); // Rapid shrink
                }
            } else {
                b.opacity = 1.0;
                b.isFading = false;
            }

            return true;
        });
    }

    /**
     * Handles complex logic for BASIC spawner mode with inheritance
     */
    public static handleBasicSpawn(
        source: Ball,
        config: SimulationConfig,
        now: number,
        pos: Vector2D,
        createBall: (overrides: Partial<Ball>) => Ball
    ): Ball | null {
        if (now - (source.lastSpawnTime || 0) < 150) return null;

        const inheritance = config.spawnerVelocityInheritance || 0.5;
        const randomFactor = config.spawnerVelocityRandomization || 0.3;
        
        const baseVel = {
            x: source.vel.x * inheritance,
            y: source.vel.y * inheritance
        };

        const randVel = {
            x: random.range(-10, 10) * randomFactor,
            y: random.range(-10, 10) * randomFactor
        };

        const finalVel = this.clampVelocity({ x: baseVel.x + randVel.x, y: baseVel.y + randVel.y }, config);

        return createBall({
            id: `spawn-basic-${source.id}-${now}`,
            pos: { ...pos },
            vel: finalVel,
            radius: config.ballRadius,
            color: source.color,
            spawnTime: now
        });
    }
}
